<template>
  <div class="rules-preview" :class="{ 'rules-preview--editable': editable }">
    <div class="rules-title" v-if="title">{{ title }}</div>
    <table
      class="rules-table"
      :style="editable ? { minWidth: `${minTableWidth}px` } : null"
    >
      <thead>
        <tr>
          <th v-if="selectable && !editable" class="w-40"></th>
          <th class="w-50">No.</th>
          <th class="w-200">Element</th>
          <th class="w-200">Condition</th>
          <th>Keyword</th>
          <th class="w-200">Action</th>
          <th v-if="editable" class="w-50"></th>
        </tr>
      </thead>
      <tbody v-for="(row, rowIndex) in displayRows" :key="row.key">
          <tr :class="{ 'rule-row--removed': editable && !row.selected }">
            <td v-if="selectable && !editable" class="text-center">
              <a-checkbox
                :checked="row.entries.every(entry => isSelected(entry.ruleIndex))"
                :indeterminate="row.partiallySelected"
                @change="toggleRow(row)"
              />
            </td>
            <td>
              <div class="d-flex align-items-center">
                <a-icon
                  class="mr-2 cursor-pointer"
                  v-if="!row.isGroup && !editable && row.occurrences.length > 0"
                  :type="isExpanded(row.key) ? 'down' : 'right'"
                  @click="toggleExpand(row.key)"
                />
                {{ rowIndex + 1 }}
              </div>
            </td>
            <td>
              <a-select
                v-if="editable"
                :value="row.rule.type"
                size="large"
                :disabled="!row.selected"
                @change="value => onRowChange(row, 'type', value)"
              >
                <a-select-option v-for="type in ruleTypes" :key="type.id" :value="type.id">
                  {{ type.name }}
                </a-select-option>
              </a-select>
              <template v-else>{{ displayRuleType(row.rule) }}</template>
            </td>
            <td>
              <a-select
                v-if="editable"
                :value="row.rule.condition"
                size="large"
                :disabled="!row.selected"
                @change="value => onRowChange(row, 'condition', value)"
              >
                <a-select-option
                  v-for="condition in conditions"
                  :key="condition.id"
                  :value="condition.id"
                >
                  {{ condition.name }}
                </a-select-option>
              </a-select>
              <template v-else>{{ displayRuleCondition(row.rule) }}</template>
            </td>
            <td>
              <a-collapse
                v-if="row.isGroup"
                class="grouped-rule-collapse"
                :bordered="false"
                expand-icon-position="right"
                accordion
              >
                <a-collapse-panel :key="row.key">
                  <template slot="header">
                    <div class="grouped-rule-header">
                      <a-input
                        v-if="editable"
                        :value="row.field"
                        size="large"
                        placeholder="Description"
                        :disabled="!row.selected"
                        @click.stop
                        @change="event => onRowFieldChange(row, event.target.value)"
                      />
                      <span v-else class="grouped-rule-prefix">{{ row.field }}</span>
                      <a-tag color="blue" class="grouped-rule-count">
                        {{ row.entries.length }} matching rules
                      </a-tag>
                      <a-tag
                        v-if="row.partiallySelected"
                        color="orange"
                        class="grouped-rule-count"
                      >
                        {{ row.selectedCount }}/{{ row.entries.length }} selected
                      </a-tag>
                    </div>
                  </template>
                  <div class="nested-rules-container">
                    <div
                      v-for="(entry, entryIndex) in row.entries"
                      :key="entry.ruleIndex"
                      class="nested-rule-item"
                      :class="{
                        'nested-rule-item--removed': !isSelected(entry.ruleIndex)
                      }"
                    >
                      <div class="nested-rule-number">
                        {{ rowIndex + 1 }}.{{ entryIndex + 1 }}
                      </div>
                      <div class="nested-rule-content">
                        <div class="nested-rule-field">{{ entry.rule.field }}</div>
                        <a-tag v-if="entry.rule.label" color="blue" class="mt-1 mb-0">
                          {{ entry.rule.label }}
                        </a-tag>
                        <div
                          v-for="(occ, occIndex) in entry.occurrences"
                          :key="occIndex"
                          class="nested-occurrence mt-1"
                        >
                          <div v-if="occ.url" class="url-text mb-1">
                            <strong>URL:</strong> {{ occ.url }}
                          </div>
                          <pre class="snippet-block m-0"><code>{{ occ.snippet }}</code></pre>
                        </div>
                      </div>
                    </div>
                  </div>
                </a-collapse-panel>
              </a-collapse>
              <template v-else-if="editable">
                <a-input
                  :value="row.field"
                  size="large"
                  placeholder="Description"
                  :disabled="!row.selected"
                  @change="event => onRowFieldChange(row, event.target.value)"
                />
              </template>
              <template v-else>
                <div style="word-break: break-all;">{{ row.field }}</div>
                <div v-if="row.rule.label" class="mt-1">
                  <a-tag color="blue" class="m-0">
                    {{ row.rule.label }}
                  </a-tag>
                </div>
              </template>
            </td>
            <td>
              <a-select
                v-if="editable"
                :value="row.rule.action"
                size="large"
                :disabled="!row.selected"
                @change="value => onRowChange(row, 'action', value)"
              >
                <a-select-option v-for="action in actions" :key="action.id" :value="action.id">
                  {{ action.name }}
                </a-select-option>
              </a-select>
              <template v-else>{{ displayRuleAction(row.rule) }}</template>
            </td>
            <td v-if="editable">
              <a-button
                size="large"
                class="remove-btn"
                :class="{ 'remove-btn--restore': !row.selected }"
                :title="removeButtonTitle(row)"
                @click="removeRow(row)"
              >
                <a-icon v-if="row.selected" type="close-circle" theme="filled" />
                <a-icon v-else type="undo" />
              </a-button>
            </td>
          </tr>
          <tr
            v-if="!row.isGroup && !editable && isExpanded(row.key) && row.occurrences.length > 0"
            :key="`exp-${row.key}`"
            class="expanded-row"
          >
            <td :colspan="editable || selectable ? 6 : 5">
              <div class="occurrences-container">
                <div
                  v-for="(occ, occIndex) in row.occurrences"
                  :key="occIndex"
                  class="occurrence-item mb-2"
                >
                  <div v-if="occ.url" class="url-text mb-1">
                    <strong>URL:</strong> {{ occ.url }}
                  </div>
                  <pre class="snippet-block m-0"><code>{{ occ.snippet }}</code></pre>
                </div>
              </div>
            </td>
          </tr>
      </tbody>
      <tbody v-if="displayRows.length === 0">
        <tr>
          <td :colspan="editable || selectable ? 6 : 5" class="text-center">{{ emptyMessage }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import {
  RULE_ACTIONS,
  RULE_CONDITIONS,
  RULE_TYPES,
  getRuleActionDisplayName,
  getRuleConditionDisplayName,
  getRuleTypeDisplayName
} from '@constants/rules';
import { groupRuleEntriesByCommonPrefix } from '@shared/utils/policyRules';

export default {
  name: 'RulesTable',
  props: {
    title: {
      type: String,
      default: '',
    },
    rules: {
      type: Array,
      default: () => [],
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    minTableWidth: {
      type: Number,
      default: 900,
    },
    groupByPrefix: {
      type: Boolean,
      default: false,
    },
    ruleEdits: {
      type: Object,
      default: () => ({}),
    },
    groupRuleEdits: {
      type: Object,
      default: () => ({}),
    },
    groupKeyPrefix: {
      type: String,
      default: '',
    },
    selectedIndices: {
      type: Set,
      default: () => new Set(),
    },
    emptyMessage: {
      type: String,
      default: 'No rules found.',
    },
    expandKey: {
      type: String,
      default: 'default',
    },
  },
  data() {
    return {
      expandedRows: {},
      actions: RULE_ACTIONS,
      conditions: RULE_CONDITIONS,
      ruleTypes: RULE_TYPES,
    };
  },
  computed: {
    ruleEntries() {
      return this.rules.map((rule, ruleIndex) => ({
        rule,
        ruleIndex,
        occurrences: (rule.occurrences || []).filter((occurrence) => occurrence.snippet)
      }));
    },
    displayRows() {
      if (!this.groupByPrefix) {
        return this.ruleEntries.map((entry) => {
          const rule = {
            ...entry.rule,
            ...this.getRuleEdit(entry)
          };
          return {
            key: `${this.expandKey}-${entry.ruleIndex}`,
            rule,
            field: rule.field,
            entries: [entry],
            occurrences: entry.occurrences,
            selected: this.isSelected(entry.ruleIndex),
            isGroup: false
          };
        });
      }

      const clusters = groupRuleEntriesByCommonPrefix(this.ruleEntries);

      return clusters.map((cluster) => {
        const selectedEntries = cluster.entries.filter((entry) =>
          this.isSelected(entry.ruleIndex)
        );
        const selectedCount = selectedEntries.length;
        const activeEntries = selectedCount > 0 ? selectedEntries : cluster.entries;
        const editedActiveEntries = activeEntries.map((entry) => ({
          ...entry,
          rule: {
            ...entry.rule,
            ...this.getRuleEdit(entry)
          }
        }));
        const firstEntry = editedActiveEntries[0];
        const activeCluster =
          editedActiveEntries.length > 1
            ? groupRuleEntriesByCommonPrefix(editedActiveEntries)[0]
            : null;
        const groupEditKey = `${this.groupKeyPrefix}-${cluster.groupId}`;
        const groupEdit = this.groupRuleEdits[groupEditKey] || {};
        const groupedRule = cluster.isGroup
          ? {
              ...firstEntry.rule,
              field: activeCluster ? activeCluster.prefix : firstEntry.rule.field,
              ...groupEdit
            }
          : firstEntry.rule;
        return {
          key: `${this.expandKey}-${cluster.entries
            .map((entry) => entry.ruleIndex)
            .join('-')}`,
          groupId: cluster.groupId,
          rule: groupedRule,
          field: groupedRule.field,
          entries: cluster.entries.map((entry) => ({
            ...entry,
            rule: {
              ...entry.rule,
              ...this.getRuleEdit(entry)
            }
          })),
          occurrences: firstEntry.occurrences,
          selected: selectedCount > 0,
          selectedCount,
          partiallySelected:
            selectedCount > 0 && selectedCount < cluster.entries.length,
          isGroup: cluster.isGroup
        };
      });
    }
  },
  watch: {
    expandKey() {
      this.resetExpanded();
    },
    rules() {
      this.resetExpanded();
    },
  },
  methods: {
    isExpanded(rowKey) {
      return !!this.expandedRows[rowKey];
    },
    toggleExpand(rowKey) {
      this.$set(this.expandedRows, rowKey, !this.expandedRows[rowKey]);
    },
    isSelected(ruleIndex) {
      return this.selectedIndices.has(ruleIndex);
    },
    getRuleEdit(entry) {
      return this.ruleEdits[
        `${this.groupKeyPrefix}-${entry.ruleIndex}`
      ] || {};
    },
    onRuleChange(ruleIndex, field, value) {
      this.$emit('change', { ruleIndex, field, value });
    },
    onRowChange(row, field, value) {
      if (!row.isGroup) {
        this.onRuleChange(row.entries[0].ruleIndex, field, value);
        return;
      }
      this.$emit('group-change', {
        groupId: row.groupId,
        field,
        value
      });
    },
    onRowFieldChange(row, value) {
      this.onRowChange(row, 'field', value);
    },
    removeButtonTitle(row) {
      if (row.partiallySelected) return 'Remove selected matching rules';
      const action = row.selected ? 'Remove' : 'Restore';
      return row.isGroup ? `${action} matching rules` : `${action} rule`;
    },
    removeRow(row) {
      const hasSelectedEntries = row.entries.some((entry) =>
        this.isSelected(entry.ruleIndex)
      );
      row.entries.forEach((entry) => {
        const selected = this.isSelected(entry.ruleIndex);
        if (
          (hasSelectedEntries && selected) ||
          (!hasSelectedEntries && !selected)
        ) {
          this.$emit('remove', entry.ruleIndex);
        }
      });
    },
    toggleRow(row) {
      const allSelected = row.entries.every((entry) => this.isSelected(entry.ruleIndex));
      row.entries.forEach((entry) => {
        if (allSelected || !this.isSelected(entry.ruleIndex)) {
          this.$emit('toggle', entry.ruleIndex);
        }
      });
    },
    resetExpanded() {
      this.expandedRows = {};
    },
    displayRuleType(rule) {
      if (rule.typeDisplayOverride) return rule.typeDisplayOverride;
      return getRuleTypeDisplayName(rule.type);
    },
    displayRuleCondition(rule) {
      return getRuleConditionDisplayName(rule.condition);
    },
    displayRuleAction(rule) {
      return getRuleActionDisplayName(rule.action);
    }
  }
};
</script>

<style lang="scss" scoped>
.rules-preview {
  overflow-x: auto;
}

.rules-preview--editable {
  background-color: #f7f7f7;
  padding: 20px;

  .rules-table {
    th,
    td {
      border: 0;
    }

    td {
      padding: 5px;
      vertical-align: top;
    }
  }

  ::v-deep .ant-select,
  ::v-deep .ant-input {
    width: 100%;
  }
}

.remove-btn {
  width: 40px;
  padding: 0;
  color: #f5222d;
  border-color: #f5222d;

  &--restore {
    color: #1890ff;
    border-color: #1890ff;
  }
}

.rule-row--removed {
  opacity: 0.65;
}

.expanded-row td {
  padding: 0;
  border-bottom: 1px solid #e8e8e8;
}

.grouped-rule-collapse {
  background: transparent;

  ::v-deep .ant-collapse-item {
    border-bottom: 0;
  }

  ::v-deep .ant-collapse-header {
    padding: 0 40px 0 0 !important;
    min-height: 40px;
  }

  ::v-deep .ant-collapse-content-box {
    padding: 0;
  }
}

.grouped-rule-header {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;

  ::v-deep .ant-input {
    flex: 1;
    min-width: 0;
  }
}

.grouped-rule-prefix {
  flex: 1;
  min-width: 0;
  word-break: break-all;
}

.grouped-rule-count {
  flex-shrink: 0;
  margin-right: 0;
}

.nested-rules-container {
  background-color: #fff;
  border-top: 1px solid #e8e8e8;
  padding: 8px 16px 0;
}

.nested-rule-item {
  display: flex;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px solid #eeeeee;

  &:last-child {
    border-bottom: 0;
  }

  &--removed {
    opacity: 0.5;
  }
}

.nested-rule-number {
  flex: 0 0 48px;
  color: rgba(0, 0, 0, 0.45);
}

.nested-rule-content {
  flex: 1;
  min-width: 0;
}

.nested-rule-field,
.nested-occurrence {
  word-break: break-all;
}

.occurrences-container {
  background-color: #fafafa;
  padding: 12px 16px;
  border-top: 1px solid #e8e8e8;
  max-width: 100%;
  overflow: hidden;
}

.occurrence-item {
  max-width: 100%;
  overflow: hidden;
}

.occurrence-item:last-child {
  margin-bottom: 0 !important;
}

.url-text {
  font-size: 13px;
  word-break: break-all;
}

.snippet-block {
  background-color: #282a36;
  color: #f8f8f2;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-all;
  margin-top: 4px;
  max-width: 100%;
}

.cursor-pointer {
  cursor: pointer;
}

.rules-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th,
  td {
    border: 1px solid #e8e8e8;
    padding: 8px;
    vertical-align: top;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  thead th {
    background: #fafafa;
    font-weight: 600;
  }
}

.rules-title {
  font-weight: 600;
  margin-bottom: 8px;
}

.w-40 {
  width: 40px;
}

.w-50 {
  width: 50px;
}

.w-200 {
  width: 200px;
}
</style>
